// Presentational step components for LoginPage — extracted to keep the page
// orchestrator within the size budget (mirrors SrDetailPage.components.tsx).
// These are pure/props-driven; all flow state + handlers live in LoginPage.tsx.

import { useState, type JSX, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { FormField } from "../components/FormField.js";
import { QRCodeSVG } from "qrcode.react";

export function LoginHeader(): JSX.Element {
  return (
    <div className="mb-8 text-center">
      <span className="text-2xl font-bold text-navy-700">HCM</span>
      <p className="mt-1 text-sm text-slate-500">Home Care Management</p>
    </div>
  );
}

interface CredentialsStepProps {
  email: string; setEmail: (v: string) => void;
  password: string; setPass: (v: string) => void;
  error: string | null; isLoading: boolean;
  onSubmit: (e: FormEvent) => void;
}

export function CredentialsStep({ email, setEmail, password, setPass, error, isLoading, onSubmit }: CredentialsStepProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-xl font-semibold text-slate-900 mb-6">Sign in</h1>
      {error && <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">{error}</div>}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField id="email" type="email" label="Email" value={email} onChange={setEmail} placeholder="you@agency.com" autoComplete="email" />
        <FormField id="password" type="password" label="Password" value={password} onChange={setPass} placeholder="••••••••" autoComplete="current-password" />
        <button type="submit" disabled={isLoading}
          className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {isLoading ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/forgot-password" className="block text-center text-sm text-navy-700 hover:text-navy-800">
          Forgot password?
        </Link>
      </form>
    </div>
  );
}

interface MfaTokenStepProps {
  totp: string; setTotp: (v: string) => void;
  error: string | null; isLoading: boolean;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}

export function MfaTokenStep({ totp, setTotp, error, isLoading, onSubmit, onBack }: MfaTokenStepProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Two-factor authentication</h1>
      <p className="text-sm text-slate-500 mb-6">Enter the 6-digit code from your authenticator app.</p>
      {error && <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">{error}</div>}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField id="totp" type="text" label="Authentication code" value={totp} onChange={setTotp}
          placeholder="000000" autoComplete="one-time-code" />
        <button type="submit" disabled={isLoading || totp.length !== 6}
          className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {isLoading ? "Verifying…" : "Verify"}
        </button>
        <button type="button" onClick={onBack} className="w-full text-sm text-slate-500 hover:text-slate-700">
          ← Back to sign in
        </button>
      </form>
    </div>
  );
}

// ── MFA Setup Flow ──────────────────────────────────────────────────────────

export function MfaSetupLoadingStep(): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
      <p className="text-sm text-slate-500 py-8" aria-busy="true">Preparing MFA setup…</p>
    </div>
  );
}

interface MfaSetupScanStepProps {
  qrUri: string;
  secret: string;
  error: string | null;
  onContinue: () => void;
  onBack: () => void;
}

export function MfaSetupScanStep({ qrUri, secret, error, onContinue, onBack }: MfaSetupScanStepProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  function copySecret(): void {
    void navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Set up two-factor authentication</h1>
      <p className="text-sm text-slate-500 mb-5">
        Scan the QR code below with <strong>Google Authenticator</strong>, <strong>Authy</strong>, or any TOTP app.
      </p>

      {error && <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">{error}</div>}

      {/* QR Code */}
      <div className="flex justify-center mb-5">
        <div className="p-3 border border-slate-200 rounded-xl bg-slate-50">
          <QRCodeSVG
            value={qrUri}
            size={180}
            level="M"
            marginSize={2}
            aria-label="MFA QR code — scan with your authenticator app"
          />
        </div>
      </div>

      {/* Manual entry fallback */}
      <div className="mb-5">
        <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 block bg-slate-100 rounded-lg px-3 py-2 text-xs font-mono text-slate-700 break-all">
            {secret}
          </code>
          <button
            type="button"
            onClick={copySecret}
            className="shrink-0 text-xs text-navy-700 hover:text-navy-800 font-medium"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mb-5">
        <strong>Important:</strong> MFA is mandatory for your role. Once set up, you'll need this app every time you sign in.
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 transition-colors"
      >
        I've scanned it — Next →
      </button>
      <button type="button" onClick={onBack} className="w-full mt-2 text-sm text-slate-500 hover:text-slate-700">
        ← Back to sign in
      </button>
    </div>
  );
}

interface MfaSetupVerifyStepProps {
  totp: string; setTotp: (v: string) => void;
  error: string | null; isLoading: boolean;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}

export function MfaSetupVerifyStep({ totp, setTotp, error, isLoading, onSubmit, onBack }: MfaSetupVerifyStepProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Confirm your authenticator</h1>
      <p className="text-sm text-slate-500 mb-6">
        Enter the 6-digit code from your authenticator app to activate MFA on your account.
      </p>
      {error && <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">{error}</div>}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField id="totp-setup" type="text" label="Authentication code" value={totp} onChange={setTotp}
          placeholder="000000" autoComplete="one-time-code" />
        <button type="submit" disabled={isLoading || totp.length !== 6}
          className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {isLoading ? "Activating…" : "Activate MFA & Sign in"}
        </button>
        <button type="button" onClick={onBack} className="w-full text-sm text-slate-500 hover:text-slate-700">
          ← Back to QR code
        </button>
      </form>
    </div>
  );
}

interface SaveRecoveryCodesStepProps {
  codes: string[];
  email: string;
  onContinue: () => void;
}

function buildRecoveryCodesFile(codes: string[], email: string): string {
  return [
    "HCM Recovery Codes",
    "==================",
    `Generated: ${new Date().toISOString()}`,
    `Account:   ${email}`,
    "",
    "Use these codes if you lose access to your authenticator app.",
    "Each code can be used ONCE.",
    "",
    ...codes.map((c, i) => `${i + 1}. ${c}`),
    "",
    "Keep this file safe and offline.",
  ].join("\n");
}

export function SaveRecoveryCodesStep({ codes, email, onContinue }: SaveRecoveryCodesStepProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  function copyCodes(): void {
    void navigator.clipboard.writeText(codes.join("\n"))
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* clipboard unavailable */ });
  }
  function downloadCodes(): void {
    const blob = new Blob([buildRecoveryCodesFile(codes, email)], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "hcm-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-xl font-semibold text-slate-900 mb-2">
        Save your recovery codes
        <span className="ml-2 text-sm text-red-600 font-normal">(shown once)</span>
      </h1>
      <p className="text-sm text-slate-500 mb-4">
        Use one of these codes if you lose access to your authenticator app. Each code works once.
      </p>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 gap-2">
          {codes.map((code) => (
            <code key={code} className="text-sm font-mono text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 text-center break-all">
              {code}
            </code>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={copyCodes}
          className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
        >
          {copied ? "Copied!" : "Copy codes"}
        </button>
        <button
          type="button"
          onClick={downloadCodes}
          className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
        >
          Download
        </button>
      </div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mb-4">
        Store these in a password manager. They are not retrievable — leaving this page without saving means you'll need an admin to reset MFA if you lose your phone.
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 transition-colors"
      >
        I've saved my recovery codes — Continue
      </button>
    </div>
  );
}
